import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getMontantsMensuels } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeMontantsPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Récupérer les paramètres de recherche depuis l'URL ou location.state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || location.state?.search || '';
    setSearchTerm(search);
  }, [location]);

  useEffect(() => {
    const loadMontants = async () => {
      try {
        const response = await getMontantsMensuels();
        setMontantsMensuels(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des montants:', error);
        notificationService.error('Erreur', 'Impossible de charger les montants mensuels');
      } finally {
        setLoading(false);
      }
    };

    loadMontants();
  }, []);

  const handlePrint = () => {
    // Attendre un peu pour s'assurer que le contenu est chargé
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Montants_Mensuels_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    // Si la fenêtre ne peut pas être fermée (pas ouverte par window.open), retourner à l'admin
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les montants selon le terme de recherche
  const filteredMontants = montantsMensuels.filter(montant => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const moisNoms = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    const moisNom = moisNoms[montant.mois - 1] || '';
    return (
      moisNom.includes(search) ||
      montant.annee?.toString().includes(search) ||
      montant.montant_mensuel?.toString().includes(search) ||
      montant.montant_hebdomadaire?.toString().includes(search)
    );
  });

  const moisNoms = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des montants mensuels...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>Liste des Montants Mensuels - Impression</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Montants Mensuels</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Mois/Année</th>
                <th>Montant mensuel (CDF)</th>
                <th>Montant hebdomadaire (CDF)</th>
                <th>Date de création</th>
              </tr>
            </thead>
            <tbody>
              {filteredMontants.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm ? 'Aucun montant trouvé' : 'Aucun montant mensuel fixé'}
                  </td>
                </tr>
              ) : (
                filteredMontants.map((montant, index) => (
                  <tr key={montant.id}>
                    <td>{index + 1}</td>
                    <td>{moisNoms[montant.mois - 1]} {montant.annee}</td>
                    <td className="text-right">
                      {parseFloat(montant.montant_mensuel).toLocaleString('fr-FR', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 0 
                      })} CDF
                    </td>
                    <td className="text-right">
                      {parseFloat(montant.montant_hebdomadaire).toLocaleString('fr-FR', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 0 
                      })} CDF
                    </td>
                    <td>{new Date(montant.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListeMontantsPrint;

